import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Dropdown, Modal, Row } from 'react-bootstrap';
import {
    calcFee,
    formatDate,
    getGrandTotal,
    getNumberContentPieces,
    getNumberPosts,
    getStatusIcon,
    getSubtotalAdditionalItems,
    getSubtotalOfferItems,
} from '../../../../../shared/functions/Functions';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { FiInfo } from 'react-icons/fi';
import HtmlToPdf from '../../../../../shared/components/generate-pdf';
import { InfluencerAcceptOfferModal } from '../offer-modal/components/InfluencerAcceptOfferModal';
import { DeclineOfferModal } from '../offer-modal/components/DeclineOfferModal';
import { ConfirmSendingOfferModal } from '../offer-modal/components/ConfirmSendingOfferModal';
import { InfluencerContractProfileInfo } from './components/InfluencerContractProfileInfo';
import { useDispatch, useSelector } from 'react-redux';
import { IDeclineOffer } from '../../dto/IDeclineOffer';
import { useTranslation } from 'react-i18next';
import { IAllStates } from '../../../../../store/rootReducer';
import { useNavigate } from 'react-router-dom';
import { useSearchQuery } from '../../../../../shared/hooks/useSearchQuery';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { scrollToTopRef } from '../../../../../utils/helpers/ScrollToTop';
import { scrollToBottomRef } from '../../../../../utils/helpers/scrollToBottomRef';
import {
    setActiveTab,
    setContractModalStatus,
    setInitialState,
    setCampaignId,
    setContractModalDetails,
} from '../../../../../store/influencer/contractInfluencerSlice';
import { InfluencerContractService } from '../../service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { ENotificationType } from '../../../../../model/campaign/Campaign';
import { gtmService } from '../../../../../utils/gtm.service';
import { ContractModalTabs } from './tabs/ContractModalTabs';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { ContractPdfDownloadButton } from '../../../../../shared/components/pdf/DownloadContractPdf';
import { CrossModalIcon, WithdrawIcon } from '../../../../../shared/Icons';

interface InfluencerContractModalProps {
    show: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    campaignId: string;
    updateList?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRocketShown?: React.Dispatch<React.SetStateAction<boolean>>;
}

export function InfluencerContractModal({
    show,
    setShowModal,
    campaignId,
    setIsRocketShown,
    updateList,
}: InfluencerContractModalProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const offerModalRef = useRef<HTMLDivElement>(null);
    const {
        campaign,
        activeTabModal,
        initialContract,
        isEditable,
        canCounter,
        contractModalStatus,
        hasLegal,
        hasFee,
        feePercentage,
        isCharity,
        isLegalChecked,
        isDistributionChecked,
        requiredFieldChangeColor,
        inviteAttachment,
    } = useSelector((state: IAllStates) => state.contractInfluencer);
    const notificationQueryType = useSearchQuery('notificationType');

    const additionalItemsSum = useMemo(
        () => getSubtotalAdditionalItems(initialContract.distribution),
        [initialContract.distribution]
    );

    const contractItemsSum = useMemo(
        () => getSubtotalOfferItems(initialContract.contractItems),
        [initialContract.contractItems]
    );

    const { grandTotalSum } = useMemo(() => {
        const baseTotal = additionalItemsSum + contractItemsSum;
        const fee = hasFee ? calcFee(baseTotal, feePercentage) : 0;
        return {
            grandTotalSum: priceSplitter(baseTotal - Math.round(fee)),
        };
    }, [additionalItemsSum, contractItemsSum, hasFee, feePercentage]);

    useEffect(() => {
        show &&
            InfluencerContractService.getContractModalDetail(campaignId)
                .then(response => {
                    notificationQueryType?.length && navigate('/influencer/offers');
                    dispatch(
                        setContractModalDetails({
                            ...response,
                            activeTabModal: notificationQueryType === ENotificationType.OFFER_COMMENT ? 5 : undefined,
                        })
                    );
                    dispatch(setCampaignId(campaignId));
                })
                .catch(err => {
                    closeModal();
                    ErrorToast(err);
                });
    }, [show]);

    // const sendInitialContractRequest = () => {
    //     setDisableButton(true);
    //     let initialOfferCopy = initialContract;
    //     const contractItems = isCharity
    //         ? initialOfferCopy.contractItems
    //         : initialOfferCopy.contractItems.filter(item => item.nbPosts > 0 && item.price > 0);
    //     const price = getGrandTotal(initialOfferCopy.contractItems, initialOfferCopy.distribution);
    //     initialOfferCopy = { ...initialOfferCopy, contractItems, price };

    //     const formData = new FormData();
    //     formData.append('body', JSON.stringify(initialOfferCopy));
    //     if (!!inviteAttachment?.length) {
    //         for (const item of inviteAttachment) {
    //             formData.append('file', item as string | Blob);
    //         }
    //     }

    //     InfluencerContractService.sendInitialContract(campaignId, formData)
    //         .then(() => {
    //             gtmService('influencer_offer_submitted', grandTotalSum);
    //             SuccessToast(t('influencer.offer_management.modal.success_initial_message'));
    //             setDisableButton(false);
    //             closeModal();
    //         })
    //         .catch(err => {
    //             ErrorToast(err);
    //             setDisableButton(false);
    //         });
    // };

    const sendCounterContractRequest = () => {
        setDisableButton(true);
        let initialConteractCopy = initialContract;
        const contractItems = isCharity
            ? initialConteractCopy.contractItems
            : initialConteractCopy.contractItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialConteractCopy.contractItems, initialConteractCopy.distribution);
        initialConteractCopy = { ...initialConteractCopy, contractItems, price };

        const formData = new FormData();
        const realFiles = Array.isArray(inviteAttachment) ? inviteAttachment.filter(item => item instanceof Blob) : [];
        const hasPlaceholder = Array.isArray(inviteAttachment)
            ? inviteAttachment.some(item => !(item instanceof Blob))
            : false;
        const isFileCleared = realFiles.length === 0 && !hasPlaceholder;

        formData.append(
            'body',
            JSON.stringify({
                ...initialConteractCopy,
                ...(isFileCleared && { clearLegal: true }),
            })
        );

        if (realFiles.length > 0) {
            for (const item of realFiles) {
                formData.append('file', item as Blob);
            }
        }

        InfluencerContractService.sendCounterContract(campaignId, formData)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_update_message'));
                gtmService('influencer_offer_submitted', grandTotalSum);
                setDisableButton(false);
                closeModal();
            })
            .catch(err => {
                ErrorToast(err);
                setDisableButton(false);
            });
    };

    const acceptContract = () => {
        InfluencerContractService.acceptContract(campaignId)
            .then(() => {
                gtmService('influencer_offer_accepted');
                setIsRocketShown && setIsRocketShown(true);
                SuccessToast(t('influencer.offer_management.modal.success_accepted_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };
    const withdrawContract = () => {
        InfluencerContractService.withdrawContract(campaignId)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_withdraw_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };
    const declineContract = () => {
        InfluencerContractService.declineContract(campaignId, declineReasons)
            .then(() => {
                SuccessToast(t('influencer.offer_management.modal.success_decline_message'));
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };

    const closeModal = () => {
        dispatch(setInitialState());
        setShowModal(false);
        if (updateList) {
            updateList(prevState => !prevState);
        }
    };

    const openSubmitConfirmModal = (event: any) => {
        event.preventDefault();
        setShowSubmitModal(true);
    };

    const setNextTab = () => {
        let currentStep;
        if (!hasLegal && activeTabModal === 2) {
            currentStep = activeTabModal + 2;
        } else {
            currentStep = activeTabModal + 1;
        }
        if (requiredFieldChangeColor && activeTabModal === 1) {
            scrollToTopRef(offerModalRef);
        } else if (!isLegalChecked && activeTabModal === 3) {
            scrollToBottomRef(offerModalRef);
        } else if (!isDistributionChecked && activeTabModal === 2) {
            scrollToBottomRef(offerModalRef);
        } else {
            scrollToTopRef(offerModalRef);
        }
        dispatch(setActiveTab(currentStep));
    };

    const changeStatus = () => {
        dispatch(setActiveTab(4));
        dispatch(setContractModalStatus('negotiate'));
    };
    const closeAcceptModal = () => {
        setShowAcceptModal(false);
    };
    const closeSubmitModal = () => {
        setShowSubmitModal(false);
    };
    const closeDeclineModal = () => {
        setShowDeclineModal(false);
    };

    return (
        <Modal
            show={show}
            onHide={closeModal}
            size="xl"
            id="offer-modal"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <form onSubmit={e => openSubmitConfirmModal(e)}>
                <Modal.Body
                    className={`${
                        canCounter || campaign?.contracts[0]?.status === 'negotiate'
                            ? 'negotiate-status-mobile'
                            : `border border-2 border-${campaign?.contracts[0]?.status}-color-mobile border-bottom-0`
                    }`}
                >
                    {campaign?.contracts[0]?.id ? (
                        <div className={`offer-status-badge ${campaign?.contracts[0]?.status} d-none d-md-flex`}>
                            {getStatusIcon(campaign?.contracts[0]?.status ?? '')}
                            <p>{t(`general.offerStatus.${campaign?.contracts[0]?.status}`)}</p>
                        </div>
                    ) : null}
                    <Row className="pb-4 h-100">
                        <Col xl={3} lg={12} className="profile-info">
                            <InfluencerContractProfileInfo />
                        </Col>
                        <Col xl={9} lg={12} style={{ height: 'calc(100% - 0px)' }}>
                            <ContractModalTabs offerModalRef={offerModalRef} />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="position-relative bg-white shadow-top">
                    <Row className="justify-content-between align-items-center w-100">
                        <Col xl={6} className="col-auto d-flex">
                            <div className="d-md-flex d-none align-items-center">
                                <CustomImageHandler
                                    photoPath={campaign?.company?.profilePhoto?.path ?? ''}
                                    altTag="User Profile"
                                    classes="middle-rounded-logo"
                                />
                                <h6 className="mb-0 ml-2">{campaign?.company?.displayName}</h6>
                            </div>
                            <div className="d-md-flex d-none align-items-center ml-3">
                                <h6 className="text-primary mb-0">{t('influencer.offers.invoicing_disclaimer')}</h6>
                                <TooltipOverlay
                                    className="ms-1"
                                    content={
                                        <p
                                            className="m-0"
                                            dangerouslySetInnerHTML={{
                                                __html: t(`influencer.offers.invoicing_disclaimer_tooltip`),
                                            }}
                                        ></p>
                                    }
                                >
                                    <FiInfo size={14} className="text-primary" />
                                </TooltipOverlay>
                            </div>
                            {activeTabModal === 4 ? (
                                <div className="d-block text-uppercase d-md-none mb-2">
                                    <div className="d-flex align-items-top">
                                        {campaign?.isCharity ? (
                                            <>
                                                {+grandTotalSum > 0 ? (
                                                    <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                                ) : (
                                                    <h5 className="text-primary-dark m-0 font-14">
                                                        {t('general.button.barter_deal')}
                                                    </h5>
                                                )}
                                            </>
                                        ) : (
                                            <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                        )}
                                        {isCharity && (
                                            <TooltipOverlay
                                                className="ms-2"
                                                content={t(`influencer.explore.barterTooltip`)}
                                            >
                                                <FiInfo size={14} />
                                            </TooltipOverlay>
                                        )}
                                    </div>
                                    <h6 className="mb-0 mt-1 font-11">
                                        {t('brand.campaign.book.offer.modal.grand_total')}
                                    </h6>
                                </div>
                            ) : null}
                        </Col>
                        <Col
                            md={12}
                            xl={6}
                            className="d-flex align-items-center justify-content-between justify-content-md-end col-auto"
                        >
                            <ContractPdfDownloadButton
                                contractId={campaign?.contracts[0]?.id}
                                status={campaign?.contracts[0]?.status}
                                isZip={true}
                            />
                            {/*{(campaign?.contracts[0]?.status === 'accepted' ||*/}
                            {/*    campaign?.contracts[0]?.status === 'completed') && (*/}
                            {/*    <HtmlToPdf*/}
                            {/*        styleContent="mr-4"*/}
                            {/*        passedOffers={campaign.contracts[0].id}*/}
                            {/*        passedCampaignId={+campaignId}*/}
                            {/*    />*/}
                            {/*)}*/}
                            {activeTabModal === 4 && (
                                <div className="align-items-center mr-md-4 mr-2 d-none d-md-flex text-uppercase">
                                    {isCharity && (
                                        <TooltipOverlay
                                            className="ms-2"
                                            content={t(`influencer.explore.barterTooltip`)}
                                        >
                                            <FiInfo size={14} />
                                        </TooltipOverlay>
                                    )}
                                    <h6 className="mb-0 mx-1">{t('brand.campaign.book.offer.modal.grand_total')}:</h6>
                                    <div className="d-flex align-items-top">
                                        {campaign?.isCharity ? (
                                            <>
                                                {+grandTotalSum > 0 ? (
                                                    <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                                ) : (
                                                    <h5 className="text-primary-dark m-0 font-14">
                                                        {t('general.button.barter_deal')}
                                                    </h5>
                                                )}
                                            </>
                                        ) : (
                                            <h5 className="text-primary mb-0 euro-symbol">{grandTotalSum}</h5>
                                        )}
                                    </div>
                                </div>
                            )}
                            <Button
                                onClick={closeModal}
                                className="mr-3 text-uppercase"
                                variant="outline-secondary"
                                size="sm"
                            >
                                {t('general.button.close')}
                            </Button>
                            <div className="d-flex">
                                {(!campaign?.contractPlaceholder?.flags?.canWithdraw && !canCounter) ||
                                contractModalStatus === 'negotiate' ? null : (
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            className="mr-2 btn-outline-primary btn-sm"
                                            variant="light"
                                            id="dropdown-basic"
                                        >
                                            {t('general.button.respond')}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {isEditable && (
                                                <Dropdown.Item
                                                    id="negotiate"
                                                    onClick={changeStatus}
                                                    className="d-flex align-items-center"
                                                >
                                                    <i className="fi fi-rr-messages-question icon-fixed me-2"></i>
                                                    {t('general.button.negotiate')}
                                                </Dropdown.Item>
                                            )}
                                            {canCounter && (
                                                <Dropdown.Item
                                                    id="decline"
                                                    onClick={() => setShowDeclineModal(true)}
                                                    className="d-flex align-items-center danger-hover"
                                                >
                                                    <CrossModalIcon width={16} height={16} className="me-2" />
                                                    {t('general.button.decline')}
                                                </Dropdown.Item>
                                            )}
                                            {campaign?.contractPlaceholder?.flags?.canWithdraw && (
                                                <Dropdown.Item id="withdraw" onClick={withdrawContract}>
                                                    <div className="d-flex align-items-center">
                                                        <WithdrawIcon width={16} height={16} className="me-2" />
                                                        {t('brand.campaign.book.contract.modal.withdraw_contract')}
                                                    </div>
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                                <Button
                                    onClick={setNextTab}
                                    className={`${activeTabModal >= 4 ? 'd-none' : ''}`}
                                    variant="primary"
                                    size="sm"
                                >
                                    {t('general.button.next')}
                                </Button>
                                <Button
                                    onSubmit={e => openSubmitConfirmModal(e)}
                                    type="submit"
                                    className={`${campaign?.contracts?.length || activeTabModal !== 4 ? 'd-none' : ''}`}
                                    variant={isCharity ? 'primary-dark' : 'primary'}
                                    size="sm"
                                >
                                    {t('general.button.submit_contract')}
                                </Button>
                                <Button
                                    type="submit"
                                    className={`${
                                        canCounter &&
                                        isEditable &&
                                        activeTabModal === 4 &&
                                        contractModalStatus === 'negotiate'
                                            ? ''
                                            : 'd-none'
                                    }`}
                                    variant="primary"
                                    size="sm"
                                >
                                    {t('general.button.submit')}
                                </Button>
                                <Button
                                    className={`${
                                        canCounter && activeTabModal === 4 && contractModalStatus === 'preview'
                                            ? ''
                                            : 'd-none'
                                    } ml-2`}
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowAcceptModal(true)}
                                >
                                    {t('general.button.accept')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Modal.Footer>
            </form>
            <InfluencerAcceptOfferModal
                show={showAcceptModal}
                closeModal={closeAcceptModal}
                nbPosts={getNumberPosts(campaign?.contractPlaceholder?.totalPlatforms)}
                grandTotal={grandTotalSum}
                action={acceptContract}
            />
            <DeclineOfferModal
                date={formatDate(campaign?.contracts[0]?.createdAt ?? '')}
                declineOffer={declineContract}
                show={showDeclineModal}
                closeModal={closeDeclineModal}
                setDeclineReason={setDeclineReasons}
                isContract={true}
            />
            <ConfirmSendingOfferModal
                // action={campaign?.contracts?.length ? sendCounterContractRequest : sendInitialContractRequest}
                action={sendCounterContractRequest}
                grandTotal={grandTotalSum}
                disableButton={disableButton}
                show={showSubmitModal}
                nbPosts={getNumberContentPieces(initialContract.contractItems)}
                isInfluencer={true}
                closeModal={closeSubmitModal}
            />
        </Modal>
    );
}
